import { useState, useCallback, useEffect } from 'react';

export interface AdminUserFilters {
  // Basic filters
  gender?: string[];
  orientation?: string[];
  role?: string[];
  
  // Location filters
  city?: string[];
  
  // Professional filters
  education?: string[];
  
  // Admin-specific filters
  onboardingStatus?: ('completed' | 'incomplete')[];
  hiddenStatus?: ('hidden' | 'visible')[];
  userRoles?: string[]; // 'admin', 'moderator', 'user'
  verificationLevel?: string[];
  planTypes?: string[];
  createdDateRange?: { start: Date | null; end: Date | null };
  
  // Toggle filters
  hasPhotos?: boolean;
  
  // Search term (centralized)
  searchTerm?: string;
}

// Validate and clean filters to remove empty arrays and invalid values
const validateFilters = (rawFilters: AdminUserFilters): AdminUserFilters => {
  const cleaned: AdminUserFilters = {};
  
  for (const [key, value] of Object.entries(rawFilters)) {
    // Skip empty arrays
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    // Keep non-empty arrays
    if (Array.isArray(value) && value.length > 0) {
      (cleaned as any)[key] = value;
      continue;
    }
    // Keep hasPhotos only if true
    if (key === 'hasPhotos' && value === true) {
      cleaned.hasPhotos = true;
      continue;
    }
    // Keep searchTerm only if non-empty string
    if (key === 'searchTerm' && typeof value === 'string' && value.trim().length > 0) {
      cleaned.searchTerm = value.trim();
      continue;
    }
    // Keep createdDateRange if has valid dates
    if (key === 'createdDateRange' && value && (value.start || value.end)) {
      cleaned.createdDateRange = value;
      continue;
    }
  }
  
  return cleaned;
};

export const useAdminFilters = () => {
  const [filters, setFilters] = useState<AdminUserFilters>(() => {
    const stored = localStorage.getItem('adminUserFilters');
    if (!stored) return {};

    try {
      const parsed = JSON.parse(stored);
      // Validate and clean stored filters
      return validateFilters(parsed);
    } catch {
      // Remove corrupted data to avoid repeated JSON parse errors and crashes
      localStorage.removeItem('adminUserFilters');
      console.warn('[useAdminFilters] Stored filters were invalid JSON and have been cleared.');
      return {};
    }
  });
  
  const [isOpen, setIsOpen] = useState(false);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('adminUserFilters', JSON.stringify(filters));
  }, [filters]);

  const updateFilter = useCallback(<K extends keyof AdminUserFilters>(
    key: K,
    value: AdminUserFilters[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  }, []);

  const clearFilters = useCallback(() => {
    setFilters({});
    localStorage.removeItem('adminUserFilters');
  }, []);

  const getActiveFiltersCount = useCallback(() => {
    let count = 0;
    
    Object.entries(filters).forEach(([key, value]) => {
      if (Array.isArray(value) && value.length > 0) {
        count++;
      } else if (key === 'createdDateRange' && value && (value.start || value.end)) {
        count++;
      }
    });
    
    return count;
  }, [filters]);

  return {
    filters,
    isOpen,
    setIsOpen,
    updateFilter,
    clearFilters,
    getActiveFiltersCount
  };
};
